import { api } from "../../lib";

export type PortalSubjectType = "physician" | "practice" | "surgery_center";
export type PortalIdentityState = "pending" | "active" | "revoked";
export type PortalGrantState = "invited" | "active" | "revoked";
export type PortalNotificationPreference =
  | "per_report"
  | "daily_digest"
  | "both";

export const DEFAULT_PORTAL_NOTIFICATION_PREFERENCE: PortalNotificationPreference =
  "daily_digest";

export interface PortalIdentitySummary {
  id: string;
  email: string;
  firstName: string | null;
  lastName: string | null;
  state: PortalIdentityState;
  lastLoginAt: string | null;
}

export interface PortalStaffRef {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PortalLinkedPhysicianRef {
  id: string;
  firstName: string;
  lastName: string;
}

export interface PortalAccessGrant {
  id: string;
  subjectType: PortalSubjectType;
  subjectId: string;
  subjectName: string;
  portalIdentity: PortalIdentitySummary;
  firstName: string | null;
  lastName: string | null;
  role: string | null;
  linkedPhysicianId: string | null;
  linkedPhysician: PortalLinkedPhysicianRef | null;
  state: PortalGrantState;
  invitedAt: string;
  activatedAt: string | null;
  revokedAt: string | null;
  invitedByStaff: PortalStaffRef | null;
  revokedByStaff: PortalStaffRef | null;
  notificationPreference: PortalNotificationPreference;
  lastDigestSentAt: string | null;
  invitationHeldForReview?: boolean;
}

export interface ListGrantsFilters {
  subjectType?: PortalSubjectType;
  subjectId?: string;
  state?: PortalGrantState | PortalGrantState[];
  search?: string;
  identityId?: string;
}

export interface CreateGrantInput {
  subjectType: PortalSubjectType;
  subjectId: string;
  email: string;
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  linkedPhysicianId?: string | null;
  notificationPreference?: PortalNotificationPreference;
}

export interface UpdateGrantInput {
  firstName?: string | null;
  lastName?: string | null;
  role?: string | null;
  email?: string;
  notificationPreference?: PortalNotificationPreference;
}

interface GrantsResponse {
  grants: PortalAccessGrant[];
}

interface GrantResponse {
  grant: PortalAccessGrant;
}

export const portalAccessService = {
  list: async (filters: ListGrantsFilters = {}) => {
    const response = await api.get<GrantsResponse>("/admin/portal-access", {
      params: filters,
    });
    return response.grants;
  },

  getById: async (grantId: string) => {
    const response = await api.get<GrantResponse>(
      `/admin/portal-access/${grantId}`
    );
    return response.grant;
  },

  create: async (input: CreateGrantInput) => {
    const response = await api.post<GrantResponse>("/admin/portal-access", {
      ...input,
      firstName: input.firstName ?? null,
      lastName: input.lastName ?? null,
      role: input.role ?? null,
      linkedPhysicianId: input.linkedPhysicianId ?? null,
    });
    return response.grant;
  },

  update: async (grantId: string, input: UpdateGrantInput) => {
    const response = await api.patch<GrantResponse>(
      `/admin/portal-access/${grantId}`,
      input
    );
    return response.grant;
  },

  revoke: async (grantId: string) => {
    const response = await api.post<GrantResponse>(
      `/admin/portal-access/${grantId}/revoke`
    );
    return response.grant;
  },

  restore: async (grantId: string) => {
    const response = await api.post<GrantResponse>(
      `/admin/portal-access/${grantId}/restore`
    );
    return response.grant;
  },

  resendInvitation: async (grantId: string) => {
    const response = await api.post<GrantResponse>(
      `/admin/portal-access/${grantId}/resend-invitation`
    );
    return response.grant;
  },

  listForSubject: async (subjectType: PortalSubjectType, subjectId: string) => {
    const subjectPath =
      subjectType === "physician"
        ? "physicians"
        : subjectType === "practice"
        ? "practices"
        : "surgery-centers";
    const response = await api.get<GrantsResponse>(
      `/admin/${subjectPath}/${subjectId}/portal-access`
    );
    return response.grants;
  },
};
